import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2, Eye, EyeOff, Mail, Lock, ArrowRight, CircleCheck, Package, Warehouse, ShieldCheck, CreditCard, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

// Client ID público do OAuth (não é segredo). Vazio = botão Google não aparece.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// O navegador valida (type="email", required, minLength); estas são as frases que o balão dele mostra.
const MENSAGENS = {
  email: { vazio: 'Email é obrigatório', invalido: 'Email inválido' },
  password: { vazio: 'Senha é obrigatória', invalido: 'Senha deve ter pelo menos 6 caracteres' },
};

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const googleClientRef = useRef(null);

  const handleGoogleToken = useCallback(async (accessToken) => {
    setLoading(true);
    try {
      const success = await loginWithGoogle({ access_token: accessToken });
      if (success) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [loginWithGoogle, navigate]);

  // Carrega o Google Identity Services e prepara o popup OAuth. O botão da
  // tela é NOSSO (o renderButton oficial vira iframe sem estilo e já quebrou
  // o clique em produção); o clique abre o popup oficial de contas do Google.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const initClient = () => {
      if (!window.google?.accounts?.oauth2) return;
      googleClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        callback: (resp) => {
          if (resp?.access_token) handleGoogleToken(resp.access_token);
        },
      });
    };

    if (window.google?.accounts?.oauth2) {
      initClient();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = initClient;
    document.head.appendChild(script);
  }, [handleGoogleToken]);

  const handleGoogleClick = () => {
    if (!googleClientRef.current) {
      toast.error('O login com o Google ainda está carregando. Tente de novo em instantes.');
      return;
    }
    googleClientRef.current.requestAccessToken();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const { email, password } = Object.fromEntries(new FormData(e.currentTarget));
    setLoading(true);
    try {
      const success = await login(email, password);
      if (success) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const submitting = loading;

  // Tremida no cartão quando o envio falha na validação; o balão do navegador leva a nossa frase.
  const [shake, setShake] = useState(false);
  const onInvalid = (e) => {
    setShake(true);
    const { name, validity } = e.target;
    e.target.setCustomValidity(validity.valueMissing ? MENSAGENS[name].vazio : MENSAGENS[name].invalido);
  };
  // A frase fica presa no campo até ele mudar: digitar volta à validação do navegador.
  const limparAviso = (e) => e.target.setCustomValidity('');

  // Holofote do cartão e paralaxe dos cartões do painel seguem o ponteiro
  const cardRef = useRef(null);
  const stageRef = useRef(null);
  const handleCardPointer = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  const handleStagePointer = (e) => {
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--px', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty('--py', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  };

  // Borda vermelha só depois que a pessoa mexeu no campo (ou tentou entrar) e ele ficou inválido.
  const fieldError = '[&:user-invalid]:border-red-500 [&:user-invalid]:focus-visible:border-red-500 [&:user-invalid]:focus-visible:ring-red-500/20';

  return (
    <div className="grid min-h-[100dvh] bg-background text-foreground lg:grid-cols-[1.1fr_1fr]">
      {/* Palco cobalto: aurora, grade de pontos e cartões de vidro flutuando */}
      <section
        ref={stageRef}
        onPointerMove={handleStagePointer}
        className="login-stage relative overflow-hidden text-white"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="login-blob login-blob-a -left-24 -top-24 h-[420px] w-[420px]" />
          <div className="login-blob login-blob-b right-[-10%] top-1/3 h-[360px] w-[360px]" />
          <div className="login-blob login-blob-c bottom-[-20%] left-1/4 h-[480px] w-[480px]" />
          <div className="login-grid absolute inset-0" />
          <img
            src="/logo-bcmed.png"
            alt=""
            className="absolute -bottom-10 -right-16 w-[min(70%,560px)] opacity-[0.06] brightness-0 invert"
          />
        </div>

        <div className="relative flex h-full flex-col px-6 pb-10 pt-8 sm:px-10 lg:px-14 lg:py-12">
          {/* login-rise anima filter; o invert fica no img de dentro */}
          <div className="login-rise self-start">
            <img src="/logo-bcmed.png" alt="BCMED" className="h-9 w-auto brightness-0 invert sm:h-10" />
          </div>

          <div className="mt-10 max-w-2xl lg:mt-auto">
            <span
              className="login-rise inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-cobalt-100 backdrop-blur"
              style={{ '--d': '80ms' }}
            >
              <span className="relative flex h-2 w-2">
                <span className="login-ping absolute inline-flex h-full w-full rounded-full bg-green-400" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
              </span>
              Sistema comercial BCMED
            </span>
            <h1
              className="login-rise mt-5 text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] [text-wrap:balance] sm:text-[48px] xl:text-[56px]"
              style={{ '--d': '160ms' }}
            >
              Pedidos, pagamentos e estoque das{' '}
              <span className="login-shimmer-text">cinco filiais</span>.
            </h1>
            <p
              className="login-rise mt-4 max-w-md text-base text-cobalt-200/90 sm:text-lg"
              style={{ '--d': '260ms' }}
            >
              Tudo num lugar só, do orçamento à nota no Tiny.
            </p>
          </div>

          {/* Cartões de vidro: só no desktop, com paralaxe */}
          <div
            aria-hidden
            className="relative mt-12 hidden h-[260px] lg:block"
            style={{
              transform: 'translate3d(calc(var(--px, 0) * -18px), calc(var(--py, 0) * -18px), 0)',
              transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <div className="login-rise absolute left-0 top-2" style={{ '--d': '420ms' }}>
              <div className="login-glass login-float w-64 rounded-2xl p-4" style={{ '--f': '0s' }}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-400/20 text-green-300">
                    <CircleCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">Pagamento confirmado</p>
                    <p className="text-xs text-cobalt-200/80">PIX · agora mesmo</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="login-rise absolute left-[300px] top-5" style={{ '--d': '560ms' }}>
              <div className="login-glass login-float w-56 rounded-2xl p-4" style={{ '--f': '-2.5s' }}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Estoque</p>
                  <Warehouse className="h-4 w-4 text-cobalt-200" />
                </div>
                <div className="mt-3 flex h-16 items-end gap-2">
                  {[70, 45, 90, 60, 80].map((h, i) => (
                    <span
                      key={i}
                      className="login-bar flex-1 rounded-t-md bg-gradient-to-t from-cobalt-500 to-cobalt-300"
                      style={{ height: `${h}%`, '--d': `${700 + i * 90}ms` }}
                    />
                  ))}
                </div>
                <p className="mt-2 text-xs text-cobalt-200/80">5 filiais sincronizadas</p>
              </div>
            </div>

            <div className="login-rise absolute left-4 top-[150px]" style={{ '--d': '700ms' }}>
              <div className="login-glass login-float w-[17rem] rounded-2xl p-4" style={{ '--f': '-4.5s' }}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cobalt-400/25 text-cobalt-100">
                    <Package className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">Pedido enviado ao Tiny</p>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                      <div className="login-bar-x h-full w-full rounded-full bg-gradient-to-r from-cobalt-400 to-green-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul
            className="login-rise mt-8 hidden flex-wrap gap-x-6 gap-y-2 text-sm text-cobalt-200/90 lg:flex"
            style={{ '--d': '820ms' }}
          >
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Acesso seguro</li>
            <li className="flex items-center gap-2"><CreditCard className="h-4 w-4" /> Pagar.me e PIX</li>
            <li className="flex items-center gap-2"><Zap className="h-4 w-4" /> Integrado ao Tiny</li>
          </ul>
        </div>
      </section>

      {/* Lado do formulário */}
      <section className="relative flex items-center justify-center overflow-hidden px-5 py-10 sm:px-10">
        <div aria-hidden className="login-halo pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full" />

        <div className="relative w-full max-w-[420px]">
          <div className="login-rise" style={{ '--d': '200ms' }}>
            <div
              ref={cardRef}
              onPointerMove={handleCardPointer}
              onAnimationEnd={(e) => e.animationName === 'login-shake' && setShake(false)}
              className={`login-card-ring rounded-[20px] ${shake ? 'login-shake' : ''}`}
            >
              <div className="relative overflow-hidden rounded-[20px] bg-card/90 p-7 shadow-pop backdrop-blur-xl sm:p-9">
                <div aria-hidden className="login-spotlight pointer-events-none absolute inset-0" />

                <div className="relative">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Lock className="h-5 w-5" />
                  </span>
                  <h2 className="mt-5 text-2xl font-semibold tracking-[-0.02em]">Bem-vindo de volta</h2>
                  <p className="mt-1 text-sm text-gray-500">Entre com seu email corporativo.</p>

                  <form onSubmit={onSubmit} className="mt-7 space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="login-field relative">
                        <Mail className="login-field-icon pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 transition-colors" />
                        <Input
                          id="login-email"
                          name="email"
                          type="email"
                          required
                          placeholder="nome@bcmed.com.br"
                          autoComplete="email"
                          onInvalid={onInvalid}
                          onChange={limparAviso}
                          data-testid="login-email-input"
                          className={`h-12 rounded-xl pl-10 transition-shadow focus-visible:shadow-[0_0_0_4px_rgb(var(--primary)/0.15)] ${fieldError}`}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="login-password">Senha</Label>
                      <div className="login-field relative">
                        <Lock className="login-field-icon pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 transition-colors" />
                        <Input
                          id="login-password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          minLength={6}
                          autoComplete="current-password"
                          onInvalid={onInvalid}
                          onChange={limparAviso}
                          data-testid="login-password-input"
                          className={`h-12 rounded-xl pl-10 pr-11 transition-shadow focus-visible:shadow-[0_0_0_4px_rgb(var(--primary)/0.15)] ${fieldError}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          tabIndex={-1}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-500 transition-colors hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="login-submit-button"
                      className="login-shine group mt-2 h-12 w-full rounded-xl text-[15px] font-semibold shadow-[0_10px_30px_-10px_rgb(var(--primary)/0.7)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-12px_rgb(var(--primary)/0.8)] active:translate-y-0"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Entrando
                        </>
                      ) : (
                        <>
                          Entrar
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </Button>
                  </form>

                  {GOOGLE_CLIENT_ID && (
                    <>
                      <div className="my-6 flex items-center gap-3 text-[12.5px] text-gray-500">
                        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-border" aria-hidden />
                        ou
                        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-border" aria-hidden />
                      </div>
                      {/* Botão NOSSO: o clique abre o popup oficial de contas do Google
                          (initTokenClient), sem iframe nem overlay. */}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGoogleClick}
                        disabled={submitting}
                        data-testid="google-login-button"
                        className="h-12 w-full gap-2.5 rounded-xl transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sheet"
                      >
                        <svg className="h-[18px] w-[18px]" viewBox="0 0 48 48" aria-hidden="true">
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                        Entrar com o Google
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <p className="login-rise mt-6 text-center text-[12.5px] text-gray-500" style={{ '--d': '400ms' }}>
            © {new Date().getFullYear()} BCMED
          </p>
        </div>
      </section>
    </div>
  );
};

export default Login;
